import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { userAPI } from "../../utils/api";

const UserManagement = () => {
  const { userProfile } = useAuth();
  const deptId = userProfile?.departmentId;
  const [scholars, setScholars] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // For bulk student creation
  const [studentForms, setStudentForms] = useState([
    { name: "", email: "", universityId: "", password: "" },
  ]);
  // For bulk scholar creation
  const [scholarForms, setScholarForms] = useState([
    { name: "", email: "", universityId: "", password: "" },
  ]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Password reset dialog state
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    userId: null,
    userName: null,
    newPassword: "",
  });

  const fetchScholars = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.getUsersByDepartment(deptId);
      const list = (res.data || []).filter((u) => u.role === "scholar");
      setScholars(list);
    } catch (err) {
      console.error(err);
      setError("Failed to load scholars.");
    } finally {
      setLoading(false);
    }
  }, [deptId]);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.getUsersByDepartment(deptId);
      const list = (res.data || []).filter((u) => u.role === "student");
      setStudents(list);
    } catch (err) {
      console.error(err);
      setError("Failed to load students.");
    } finally {
      setLoading(false);
    }
  }, [deptId]);

  useEffect(() => {
    if (deptId) {
      fetchScholars();
      fetchStudents();
    }
  }, [deptId, fetchScholars, fetchStudents]);

  // Delete user function
  const handleDeleteUser = async (userId, userName, userRole) => {
    if (window.confirm(`Are you sure you want to delete ${userName}?`)) {
      try {
        setLoading(true);
        await userAPI.delete(userId);
        setSuccess(`${userRole} ${userName} deleted successfully.`);

        // Refresh the appropriate list
        if (userRole === "scholar") {
          fetchScholars();
        } else if (userRole === "student") {
          fetchStudents();
        }
      } catch (err) {
        console.error(err);
        const msg =
          err.response?.data?.message || `Failed to delete ${userRole}.`;
        setError(msg);
      } finally {
        setLoading(false);
      }
    }
  };

  // Reset password functions
  const handleOpenPasswordDialog = (userId, userName) => {
    setPasswordDialog({
      open: true,
      userId,
      userName,
      newPassword: "",
    });
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialog({
      open: false,
      userId: null,
      userName: null,
      newPassword: "",
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordDialog((prev) => ({
      ...prev,
      newPassword: e.target.value,
    }));
  };

  const handleResetPassword = async () => {
    if (!passwordDialog.newPassword) {
      setError("Please enter a new password.");
      return;
    }

    try {
      setLoading(true);
      await userAPI.resetPassword(passwordDialog.userId, {
        newPassword: passwordDialog.newPassword,
      });
      setSuccess(`Password reset successfully for ${passwordDialog.userName}.`);
      handleClosePasswordDialog();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Failed to reset password.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentFormChange = (index, field, value) => {
    const updated = [...studentForms];
    updated[index][field] = value;
    setStudentForms(updated);
  };

  const handleScholarFormChange = (index, field, value) => {
    const updated = [...scholarForms];
    updated[index][field] = value;
    setScholarForms(updated);
  };

  const addStudentForm = () => {
    setStudentForms([
      ...studentForms,
      { name: "", email: "", universityId: "", password: "" },
    ]);
  };

  const addScholarForm = () => {
    setScholarForms([
      ...scholarForms,
      { name: "", email: "", universityId: "", password: "" },
    ]);
  };

  const removeStudentForm = (index) => {
    if (studentForms.length > 1) {
      const updated = studentForms.filter((_, i) => i !== index);
      setStudentForms(updated);
    }
  };

  const removeScholarForm = (index) => {
    if (scholarForms.length > 1) {
      const updated = scholarForms.filter((_, i) => i !== index);
      setScholarForms(updated);
    }
  };

  const handleBulkStudentSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate all forms first
      const validStudents = studentForms.filter(
        (student) =>
          student.name &&
          student.email &&
          student.universityId &&
          student.password
      );

      if (validStudents.length === 0) {
        setError("Please fill in at least one complete student form.");
        return;
      }

      // Call bulk creation API
      const res = await userAPI.createBulkStudents({
        students: validStudents,
        departmentId: deptId,
      });

      setSuccess(`Successfully created ${res.data.created} student accounts.`);
      setStudentForms([
        { name: "", email: "", universityId: "", password: "" },
      ]);
      fetchStudents();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Failed to create students.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkScholarSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate all forms first
      const validScholars = scholarForms.filter(
        (scholar) =>
          scholar.name &&
          scholar.email &&
          scholar.universityId &&
          scholar.password
      );

      if (validScholars.length === 0) {
        setError("Please fill in at least one complete scholar form.");
        return;
      }

      // Call bulk creation API
      const res = await userAPI.createBulkScholars({
        scholars: validScholars,
        departmentId: deptId,
      });

      setSuccess(`Successfully created ${res.data.created} scholar accounts.`);
      setScholarForms([
        { name: "", email: "", universityId: "", password: "" },
      ]);
      fetchScholars();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Failed to create scholars.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6">User Management</Typography>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="Bulk Scholar Creation" />
        <Tab label="Scholars List" />
        <Tab label="Bulk Student Creation" />
        <Tab label="Students List" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Bulk Scholar Creation
          </Typography>
          <Box component="form" onSubmit={handleBulkScholarSubmit}>
            {scholarForms.map((scholar, index) => (
              <Box
                key={index}
                sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2">
                    Scholar {index + 1}
                  </Typography>
                  {scholarForms.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeScholarForm(index)}
                      sx={{ ml: "auto" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Name"
                    value={scholar.name}
                    onChange={(e) =>
                      handleScholarFormChange(index, "name", e.target.value)
                    }
                    required
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={scholar.email}
                    onChange={(e) =>
                      handleScholarFormChange(index, "email", e.target.value)
                    }
                    required
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="University ID"
                    value={scholar.universityId}
                    onChange={(e) =>
                      handleScholarFormChange(
                        index,
                        "universityId",
                        e.target.value
                      )
                    }
                    required
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={scholar.password}
                    onChange={(e) =>
                      handleScholarFormChange(index, "password", e.target.value)
                    }
                    required
                    sx={{ minWidth: 150 }}
                  />
                </Box>
              </Box>
            ))}

            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addScholarForm}
                sx={{ mr: 2 }}
              >
                Add Another Scholar
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading
                  ? "Creating Scholars..."
                  : `Create ${scholarForms.length} Scholar(s)`}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Scholars List
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>University ID</TableCell>
                  <TableCell>Password</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scholars.map((s) => (
                  <TableRow key={s.id || s._id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>{s.universityId}</TableCell>
                    <TableCell>••••••••</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPasswordDialog(s._id, s.name)}
                      >
                        <KeyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteUser(s._id, s.name, "Scholar")
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {scholars.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No scholars found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Bulk Student Creation
          </Typography>
          <Box component="form" onSubmit={handleBulkStudentSubmit}>
            {studentForms.map((student, index) => (
              <Box
                key={index}
                sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2">
                    Student {index + 1}
                  </Typography>
                  {studentForms.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeStudentForm(index)}
                      sx={{ ml: "auto" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Name"
                    value={student.name}
                    onChange={(e) =>
                      handleStudentFormChange(index, "name", e.target.value)
                    }
                    required
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={student.email}
                    onChange={(e) =>
                      handleStudentFormChange(index, "email", e.target.value)
                    }
                    required
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="University ID"
                    value={student.universityId}
                    onChange={(e) =>
                      handleStudentFormChange(
                        index,
                        "universityId",
                        e.target.value
                      )
                    }
                    required
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={student.password}
                    onChange={(e) =>
                      handleStudentFormChange(index, "password", e.target.value)
                    }
                    required
                    sx={{ minWidth: 150 }}
                  />
                </Box>
              </Box>
            ))}

            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addStudentForm}
                sx={{ mr: 2 }}
              >
                Add Another Student
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading
                  ? "Creating Students..."
                  : `Create ${studentForms.length} Student(s)`}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Students List
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>University ID</TableCell>
                  <TableCell>Password</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((s) => (
                  <TableRow key={s.id || s._id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>{s.universityId}</TableCell>
                    <TableCell>••••••••</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPasswordDialog(s._id, s.name)}
                      >
                        <KeyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteUser(s._id, s.name, "Student")
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {students.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No students found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess("")}
      >
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>

      {/* Password Reset Dialog */}
      <Dialog
        open={passwordDialog.open}
        onClose={handleClosePasswordDialog}
        aria-labelledby="password-reset-dialog-title"
      >
        <DialogTitle id="password-reset-dialog-title">
          Reset Password
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter a new password for {passwordDialog.userName}:
          </Typography>
          <TextField
            label="New Password"
            type="password"
            value={passwordDialog.newPassword}
            onChange={handlePasswordChange}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserManagement;
